import { createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { shouldNotSkipOfferGeneration } from '../../data/BusinessConstants';
import {
  RFQ_LINEITEM_CLASS,
  RFQ_REQUEST_CLASS,
  RFQ_RESPONSE_CLASS} from '../../data/RFQClassDefinitionsConstants'
import { EMPTY_QUOTE_ENTRY } from '../../data/RFXCommonStepsData';
import { RFXRequest } from '../../interfaces/RFXBackendModel';
import { RFXSupplierBackendModel } from '../../interfaces/RFXResponseForm';
import { RootState } from '../../store';

export interface SupplierViewPageState {
  isRFXReqLoading: boolean;
  currentRound: number;
  vendorId: string;
  reqData: RFXRequest[];
  supplierBackendModel: RFXSupplierBackendModel;
}

const initialState: SupplierViewPageState = {
  isRFXReqLoading: true,
  currentRound: 0,
  vendorId: '',
  reqData: [
    {
      rfxRequestRevisionedId: {
        id: {
          value: ''
        },
        revision: 0
      },
      rfxRequestVersion: 0,
      rfxRequestClassId: {
        value: RFQ_REQUEST_CLASS
      },
      rfxResponseClassId: {
        value: RFQ_RESPONSE_CLASS
      },
      rfxLineItemClassId: {
        value: RFQ_LINEITEM_CLASS
      },
      buyer: {
        value: ''
      },
      region: {
        value: ''
      },
      beginAt: 0,
      dueAt: 0,
      name: '',
      type: '',
      currency: '',
      suppliers: [],
      documents: [],
      generalInformation: {
        overview: '',
        processDetail: '',
      },
      lifeCycle: {
        lifeCycleGraphId: 'DEFAULT_AWS_RFQ_REQUEST',
        lifeCycleState: {
          name: 'Draft',
          version: 1
        },
        lifeCycleVersion: 1
      }
    }
  ],
  supplierBackendModel: {
    round: 0,
    requestId: '',
    requestBusinessId: '',
    rfxType: '',
    rfxName: '',
    buyer: '',
    requestStatus: '',
    beginDate: '1614971719333',
    dueDate: '1614971719333',
    needByDate: '1614971719333',
    region: '',
    currency: '',
    quoteTemplateType: '',
    rfxLineItemClassId: '',
    overview: '',
    processDetails: '',
    supplier: '',
    daysRemaining: 0,
    responseId: '',
    responseRevision: 0,
    responseVersion: 0,
    responseStatus: '',
    requestQuote: cloneDeep(EMPTY_QUOTE_ENTRY),
    responseQuotes: [],
    requestDocuments: [],
    responseDocuments: [],
    contacts: [],
    totalAcquisitionCost: null,
    lineItems: []
  }
};

const supplierViewPageSlice = createSlice({
  name: 'supplierViewPage',
  initialState,
  reducers: {
    setIsRFXReqLoading(state, action) {
      state.isRFXReqLoading = action.payload;
    },
    setCurrentRound(state, action) {
      state.currentRound = action.payload;
    },
    setVendorId(state, action) {
      state.vendorId = action.payload;
    },
    setReqData(state, action) {
      state.reqData = action.payload;
    },
    setSupplierBackendModel(state, action) {
      state.supplierBackendModel = action.payload;
    }
  }
});

export const {
  setIsRFXReqLoading,
  setReqData,
  setSupplierBackendModel,
  setCurrentRound,
  setVendorId
} = supplierViewPageSlice.actions;

export const selectIsRFXReqLoading = (state: RootState) =>
  state.supplierViewPage.isRFXReqLoading;

export const selectCurrentRound = (state: RootState) =>
  state.supplierViewPage.currentRound;

export const selectVendorId = (state: RootState) => state.supplierViewPage.vendorId;

export const selectReqData = (state: RootState) => state.supplierViewPage.reqData;

export const selectSupplierBackendModel = (state: RootState) =>
  state.supplierViewPage.supplierBackendModel;

export const selectShouldShowQuoteValidity = (state: RootState) => {
  const requestBusinessId = state.supplierViewPage.supplierBackendModel.requestBusinessId;
  return shouldNotSkipOfferGeneration(requestBusinessId);
}

export default supplierViewPageSlice.reducer;
